import { refreshAdminTaskCount } from '../general/admin-tasks';
import { useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Rating } from 'primereact/rating';
import { Dropdown } from 'primereact/dropdown';
import { Paginator } from 'primereact/paginator';
import { useApi } from '../providers/api-provider';
import { useAuth } from '../providers/auth-provider';
import { AdminReviewDto, AdminReviewsDto } from '../general/dtos';
import { useBookmarkSelection } from '../general/bookmark-selection';
import { ContentPage } from '../components/content-page';
import '../assets/admin-reviews.css';

const dateLabel = (value: string) => new Date(value).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

export default function AdminReviewsPage() {
    const api = useApi();
    const identity = useAuth().getUser()?.sub;
    const [pageKey, selectPage] = useBookmarkSelection('page');
    const [reviewKey, selectReview] = useBookmarkSelection('review');
    const [courseKey, selectCourse] = useBookmarkSelection('course');
    const [sharedKey, selectShared] = useBookmarkSelection('shared');
    const [approvedKey, selectApproved] = useBookmarkSelection('approved');
    const [starsKey, selectStars] = useBookmarkSelection('stars');
    const [sizeKey, selectSize] = useBookmarkSelection('page_size');
    const shared = ['0', '1'].includes(sharedKey ?? '') ? sharedKey! : 'all';
    const approved = ['all', '0', '1'].includes(approvedKey ?? '') ? approvedKey! : '0';
    const stars = /^[1-5]$/.test(starsKey ?? '') ? starsKey! : 'all';
    const pageSize = ['10', '25', '50'].includes(sizeKey ?? '') ? Number(sizeKey) : 10;
    const page = pageKey && /^[1-9][0-9]{0,5}$/.test(pageKey) ? Number(pageKey) : 1;
    const [queue, setQueue] = useState<AdminReviewsDto | null>(null);
    const [loading, setLoading] = useState(true);
    const [listError, setListError] = useState('');
    const [review, setReview] = useState<AdminReviewDto | null>(null);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [detailsError, setDetailsError] = useState('');
    const [approvalError, setApprovalError] = useState('');
    const [approving, setApproving] = useState(false);
    const [notice, setNotice] = useState('');
    const [revision, setRevision] = useState(0);
    const [detailsRevision, setDetailsRevision] = useState(0);
    const mounted = useRef(true);
    const saving = useRef(false);
    const current = useRef({ reviewKey, identity });
    current.current = { reviewKey, identity };
    useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

    useEffect(() => {
        if (!api) return;
        const controller = new AbortController();
        setLoading(true); setListError('');
        api.getAdminReviews(page, { course: courseKey, shared, approved, stars }, pageSize, controller.signal).then(result => {
            if (!controller.signal.aborted) setQueue(result);
        }).catch(() => {
            if (!controller.signal.aborted) setListError('Could not load reviews. Please try again.');
        }).finally(() => { if (!controller.signal.aborted) setLoading(false); });
        return () => controller.abort();
    }, [api, page, courseKey, shared, approved, stars, pageSize, revision, identity]);

    useEffect(() => {
        const controller = new AbortController();
        setReview(null); setDetailsError(''); setApprovalError(''); setDetailsLoading(false);
        if (!api || !reviewKey) return;
        setDetailsLoading(true);
        api.getAdminReview(reviewKey, controller.signal).then(result => {
            if (!controller.signal.aborted) setReview(result);
        }).catch((error: any) => {
            if (!controller.signal.aborted) setDetailsError(error?.response?.status === 404
                ? 'This review is no longer available.' : 'Could not load the review. Please try again.');
        }).finally(() => { if (!controller.signal.aborted) setDetailsLoading(false); });
        return () => controller.abort();
    }, [api, reviewKey, detailsRevision, identity]);

    const changeApproval = async () => {
        if (!api || !review || (!review.approved_by_admin && !review.share_with_others) || saving.current) return;
        const approvedReview = review;
        saving.current = true; setApproving(true); setApprovalError(''); setNotice('');
        try {
            if (approvedReview.approved_by_admin) await api.unapproveReview(approvedReview.id);
            else await api.approveReview(approvedReview.id);
            if (mounted.current && current.current.identity === identity) {
                setNotice(approvedReview.approved_by_admin ? 'Review unapproved and removed from the public Reviews page.' : 'Review approved and available on the public Reviews page.');
                if (current.current.reviewKey === String(approvedReview.id)) selectReview(null, true);
                setRevision(value => value + 1);
                refreshAdminTaskCount();
            }
        } catch (error: any) {
            if (mounted.current && current.current.identity === identity && current.current.reviewKey === String(approvedReview.id))
                setApprovalError(error?.response?.data?.message || (approvedReview.approved_by_admin ? 'Could not unapprove the review. Please try again.' : 'Could not approve the review. Please try again.'));
        } finally {
            saving.current = false;
            if (mounted.current) setApproving(false);
        }
    };

    return <div className="review-moderation">
        {notice && <p role="status" className="review-moderation-notice">{notice}</p>}
        <ContentPage pageId="admin-reviews" pageTitle="Review moderation" pageTitleLevel="h1"
            leftPanelTitle="Reviews" rightPanelTitle="Review details" mobileTabs
            initialMobilePanel={reviewKey ? 'right' : 'left'} mobileBackLabel="Back to reviews"
            leftContent={showDetails => <section className="review-moderation-pane" aria-label="Review list">
                <div className="review-moderation-filters admin-toolbar">
                    <div><label htmlFor="admin-review-course">Course</label><Dropdown inputId="admin-review-course" value={courseKey}
                        options={queue?.courses ?? []} optionLabel="course_name" optionValue="course_key" placeholder="All courses" showClear filter disabled={approving}
                        panelClassName="review-moderation-course-options" panelStyle={{ width: 'max-content', maxWidth: 'calc(100vw - 2rem)' }}
                        onChange={event => { selectCourse(event.value ?? null); selectPage(null, true); }} /></div>
                    <div><label htmlFor="admin-review-shared">Sharing</label><Dropdown inputId="admin-review-shared" value={shared}
                        options={[{ label: 'All', value: 'all' }, { label: 'Allowed', value: '1' }, { label: 'Private', value: '0' }]} disabled={approving}
                        onChange={event => { selectShared(event.value === 'all' ? null : event.value); selectPage(null, true); }} /></div>
                    <div><label htmlFor="admin-review-approved">Approval</label><Dropdown inputId="admin-review-approved" value={approved}
                        options={[{ label: 'All', value: 'all' }, { label: 'Not approved', value: '0' }, { label: 'Approved', value: '1' }]} disabled={approving}
                        onChange={event => { selectApproved(event.value === '0' ? null : event.value); selectPage(null, true); }} /></div>
                    <div><label htmlFor="admin-review-stars">Rating</label><Dropdown inputId="admin-review-stars" value={stars}
                        options={[{ label: 'All ratings', value: 'all' }, ...[5, 4, 3, 2, 1].map(value => ({ label: `${value} ${value === 1 ? 'star' : 'stars'}`, value: String(value) }))]} disabled={approving}
                        onChange={event => { selectStars(event.value === 'all' ? null : event.value); selectPage(null, true); }} /></div>
                    <Button icon="pi pi-refresh" aria-label="Refresh reviews" text size="small" disabled={loading || approving} onClick={() => { setRevision(value => value + 1); setDetailsRevision(value => value + 1); }} />
                </div>
                {listError ? <div role="alert" className="p-3">{listError} <Button label="Retry reviews" text onClick={() => setRevision(value => value + 1)} /></div>
                    : <DataTable value={queue?.data ?? []} dataKey="id" lazy className="review-moderation-table"
                        loading={loading || approving} scrollable scrollHeight="flex" selectionMode="single"
                        selection={queue?.data.find(item => String(item.id) === reviewKey) ?? null}
                        onSelectionChange={event => {
                            if (approving) return;
                            if (event.value) { selectReview(String((event.value as AdminReviewDto).id)); setNotice(''); }
                            if (event.value || reviewKey) showDetails();
                        }}
                        emptyMessage="No written reviews match these filters.">
                        <Column field="created_at" header="Submitted" body={(row: AdminReviewDto) => <time dateTime={row.created_at}>{dateLabel(row.created_at)}</time>} />
                        <Column field="course_name" header="Course" />
                        <Column field="stars" header="Rating" body={(row: AdminReviewDto) => `${row.stars}/5`} />
                        <Column header="Sharing" body={(row: AdminReviewDto) => row.share_with_others ? 'Allowed' : 'Private'} />
                        <Column header="Approval" body={(row: AdminReviewDto) => row.approved_by_admin ? 'Approved' : 'Not approved'} />
                    </DataTable>}
                <div className="review-moderation-pagination">
                    <div className="review-moderation-page-size admin-toolbar"><label htmlFor="admin-review-page-size">Page size</label>
                        <Dropdown inputId="admin-review-page-size" value={pageSize} options={[10, 25, 50]} disabled={approving} onChange={event => {
                            selectSize(event.value === 10 ? null : String(event.value)); selectPage(null, true);
                        }} />
                    </div>
                    <Paginator first={((queue?.page ?? page) - 1) * pageSize} rows={pageSize} totalRecords={queue?.total ?? 0}
                        template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
                        currentPageReportTemplate="{totalRecords} reviews" onPageChange={event => {
                            if (!approving) selectPage(event.page === 0 ? null : String(event.page + 1));
                        }} />
                </div>
            </section>}
            rightContent={<section className="review-moderation-pane" aria-labelledby="review-details-heading">
                <div className="review-moderation-pane-heading"><h2 id="review-details-heading">Review details</h2></div>
                <div className="review-moderation-details">
                    {detailsLoading ? <div role="status"><ProgressSpinner aria-label="Loading review details" /></div>
                        : detailsError ? <div role="alert">{detailsError} <Button label="Retry review details" text onClick={() => setDetailsRevision(value => value + 1)} /></div>
                        : !review ? <p className="text-color-secondary">Select a review from the table to read its details.</p>
                        : <>
                            <h3>{review.course_name}</h3>
                            <Rating value={review.stars} readOnly cancel={false} aria-label={`${review.stars} out of 5 stars`} />
                            <dl className="review-moderation-meta">
                                <dt>Submitted</dt><dd><time dateTime={review.created_at}>{dateLabel(review.created_at)}</time></dd>
                                <dt>Reviewer</dt><dd>{review.reviewer_name || 'Learner'}{review.reviewer_email && <span className="review-moderation-email">{review.reviewer_email}</span>}</dd>
                                <dt>Sharing</dt><dd>{review.share_with_others ? 'Allowed to share with other users' : 'Private feedback'}</dd>
                                <dt>Admin approval</dt><dd>{review.approved_by_admin ? 'Approved' : 'Not approved'}</dd>
                            </dl>
                            <blockquote>{review.text}</blockquote>
                            {!review.share_with_others && <p className="text-color-secondary">The learner has not allowed this review to be shared.</p>}
                            {approvalError && <p role="alert">{approvalError}</p>}
                            {(review.approved_by_admin || review.share_with_others) && <Button label={review.approved_by_admin ? 'Unapprove review' : 'Approve review'}
                                icon={review.approved_by_admin ? 'pi pi-times' : 'pi pi-check'} severity={review.approved_by_admin ? 'warning' : undefined}
                                loading={approving} disabled={approving} onClick={() => void changeApproval()} />}
                        </>}
                </div>
            </section>} />
    </div>;
}
